import { FormEvent, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { clientsApi } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

type Client = {
  id: string;
  name: string;
  cif: string;
  phone?: string;
  email?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  sales?: Array<{ id: string; amount: number | string }>;
};

type ClientFormData = {
  name: string;
  cif: string;
  phone: string;
  email: string;
  notes: string;
};

const EMPTY_FORM: ClientFormData = {
  name: '',
  cif: '',
  phone: '',
  email: '',
  notes: '',
};

function toPayload(form: ClientFormData) {
  const phone = form.phone.trim();
  const email = form.email.trim();
  const notes = form.notes.trim();

  return {
    name: form.name.trim(),
    cif: form.cif.trim(),
    phone: phone || undefined,
    email: email || undefined,
    notes: notes || undefined,
  };
}

export function ClientsPage() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<ClientFormData>(EMPTY_FORM);
  const [editingClientId, setEditingClientId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const limit = 10;

  const { data, isLoading } = useQuery({
    queryKey: ['clients', page, search],
    queryFn: () =>
      clientsApi.getAll({
        page,
        limit,
        search: search.trim() || undefined,
        sortBy: 'createdAt',
        sortOrder: 'DESC',
      }),
  });

  const clients: Client[] = data?.data.data ?? [];
  const total = data?.data.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const createMutation = useMutation({
    mutationFn: (payload: ReturnType<typeof toPayload>) => clientsApi.create(payload),
    onSuccess: () => {
      toast.success('Cliente creado');
      setForm(EMPTY_FORM);
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ReturnType<typeof toPayload> }) =>
      clientsApi.update(id, payload),
    onSuccess: () => {
      toast.success('Cliente actualizado');
      setForm(EMPTY_FORM);
      setEditingClientId(null);
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => clientsApi.delete(id),
    onSuccess: () => {
      toast.success('Cliente eliminado');
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });

  const isSubmitting =
    createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  const currentActionLabel = useMemo(
    () => (editingClientId ? 'Guardar cambios' : 'Crear cliente'),
    [editingClientId],
  );

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingClientId(null);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const payload = toPayload(form);

    if (!payload.name || !payload.cif) {
      toast.error('Nombre y CIF son obligatorios');
      return;
    }

    if (editingClientId) {
      await updateMutation.mutateAsync({ id: editingClientId, payload });
      return;
    }

    await createMutation.mutateAsync(payload);
  };

  const handleEdit = (client: Client) => {
    setEditingClientId(client.id);
    setForm({
      name: client.name || '',
      cif: client.cif || '',
      phone: client.phone || '',
      email: client.email || '',
      notes: client.notes || '',
    });
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('¿Eliminar este cliente?')) return;
    await deleteMutation.mutateAsync(id);
  };

  const getSalesCount = (client: Client) => client.sales?.length || 0;
  const getSalesTotal = (client: Client) =>
    (client.sales || []).reduce((totalAmount, sale) => totalAmount + Number(sale.amount), 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Clientes</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            CRUD completo para la tabla de clientes.
          </p>
        </div>
      </div>

      <div className="grid items-start gap-6 xl:grid-cols-[420px_minmax(0,1fr)]">
        <section className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5 xl:sticky xl:top-24">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {editingClientId ? 'Editar cliente' : 'Nuevo cliente'}
          </h2>

          <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Nombre *</label>
              <Input
                value={form.name}
                onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                placeholder="Empresa SL"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">CIF *</label>
              <Input
                value={form.cif}
                onChange={(event) => setForm((prev) => ({ ...prev, cif: event.target.value }))}
                placeholder="B12345678"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Teléfono</label>
              <Input
                value={form.phone}
                onChange={(event) => setForm((prev) => ({ ...prev, phone: event.target.value }))}
                placeholder="+34..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <Input
                type="email"
                value={form.email}
                onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
                placeholder="cliente@empresa.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Notas</label>
              <textarea
                value={form.notes}
                onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                rows={4}
                placeholder="Información adicional"
              />
            </div>

            <div className="flex items-center gap-2">
              <Button type="submit" loading={isSubmitting}>
                {currentActionLabel}
              </Button>
              {editingClientId && (
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancelar edición
                </Button>
              )}
            </div>
          </form>
        </section>

        <div className="min-w-0">
          <section className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5 space-y-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Listado ({total})
              </h2>
              <Input
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
                placeholder="Buscar por nombre, CIF o email"
                className="md:max-w-sm"
              />
            </div>

            {isLoading ? (
              <p className="text-sm text-gray-500">Cargando clientes...</p>
            ) : clients.length === 0 ? (
              <p className="text-sm text-gray-500">No hay clientes para mostrar.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-800 text-left">
                      <th className="py-2 pr-4">Nombre</th>
                      <th className="py-2 pr-4">CIF</th>
                      <th className="py-2 pr-4">Contacto</th>
                      <th className="py-2 pr-4">Ventas</th>
                      <th className="py-2 pr-4">Creado</th>
                      <th className="py-2">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clients.map((client) => (
                      <tr key={client.id} className="border-b border-gray-100 dark:border-gray-800">
                        <td className="py-3 pr-4">
                          <p className="font-medium">{client.name}</p>
                          {client.notes && <p className="text-xs text-gray-500">{client.notes}</p>}
                        </td>
                        <td className="py-3 pr-4">{client.cif}</td>
                        <td className="py-3 pr-4">
                          <p>{client.phone || '-'}</p>
                          <p className="text-xs text-gray-500">{client.email || '-'}</p>
                        </td>
                        <td className="py-3 pr-4">
                          <p>{getSalesCount(client)} operaciones</p>
                          <p className="text-xs text-gray-500">
                            {formatCurrency(getSalesTotal(client))}
                          </p>
                        </td>
                        <td className="py-3 pr-4">{formatDate(client.createdAt)}</td>
                        <td className="py-3">
                          <div className="flex items-center gap-2">
                            <Button size="sm" variant="outline" onClick={() => handleEdit(client)}>
                              Editar
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              loading={deleteMutation.isPending}
                              onClick={() => handleDelete(client.id)}
                            >
                              Eliminar
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500">
                Página {page} de {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  disabled={page === 1}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={page >= totalPages}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
